import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icoon } from './Icoon';
import { createPortal } from 'react-dom';
import type { StatusToon } from '@ep/core';

export function Modal({ titel, onSluit, children, voet, breedte, extraKop }: { titel: ReactNode; onSluit: () => void; children: ReactNode; voet?: ReactNode; breedte?: 'breed' | 'vol' | 'xl'; extraKop?: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onSluit();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onSluit]);
  // Altijd direct onder <body>: zo erft een modal geen stijl van waar hij geopend wordt (bijv. de bovenbalk)
  return createPortal(
    <div className="modal-achter" onMouseDown={(e) => e.target === e.currentTarget && onSluit()}>
      <div className={`modal ${breedte ?? ''}`} role="dialog" aria-modal="true">
        <div className="modal-kop">
          <h2 className="groei">{titel}</h2>
          {extraKop}
          <button className="icoon-knop" onClick={onSluit} aria-label="Sluiten"><Icoon naam="x" maat={18} /></button>
        </div>
        <div className="modal-inhoud">{children}</div>
        {voet && <div className="modal-voet">{voet}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Veld({ label, children, className, hint }: { label: string; children: ReactNode; className?: string; hint?: ReactNode }) {
  return (
    <label className={`veld ${className ?? ''}`}>
      <span className="lbl">{label}</span>
      {children}
      {hint && <span className="klein">{hint}</span>}
    </label>
  );
}

export function Badge({ toon, children, title }: { toon: StatusToon | 'grijs'; children: ReactNode; title?: string }) {
  return (
    <span className={`badge toon-${toon}`} title={title}>
      {children}
    </span>
  );
}

export function Balk({ waarde, max }: { waarde: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (waarde / max) * 100) : 0;
  return (
    <div className={`balk ${max > 0 && waarde > max ? 'over' : ''}`}>
      <div style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Verwijderknop met tweede klik ter bevestiging (zoals in de Power App). */
export function VerwijderKnop({ onBevestig, label = 'Verwijderen', klein }: { onBevestig: () => void; label?: string; klein?: boolean }) {
  const [zeker, setZeker] = useState(false);
  useEffect(() => {
    if (!zeker) return;
    const t = setTimeout(() => setZeker(false), 4000);
    return () => clearTimeout(t);
  }, [zeker]);
  return (
    <button
      type="button"
      className={`knop gevaar ${zeker ? 'bevestig' : ''} ${klein ? 'klein' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (zeker) {
          setZeker(false);
          onBevestig();
        } else setZeker(true);
      }}
    >
      {zeker ? 'Zeker weten?' : label === '🗑' ? <Icoon naam="trash-2" maat={14} /> : label}
    </button>
  );
}

export interface Optie { waarde: string; label: string }

export function MultiSelect({ opties, waarden, onChange, placeholder }: { opties: Optie[]; waarden: string[]; onChange: (w: string[]) => void; placeholder?: string }) {
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', k);
    return () => document.removeEventListener('mousedown', k);
  }, []);
  const gefilterd = useMemo(() => opties.filter((o) => o.label.toLowerCase().includes(zoek.toLowerCase())), [opties, zoek]);
  return (
    <div className="multi" ref={ref}>
      <div className="multi-veld" onClick={() => setOpen((o) => !o)}>
        {waarden.length === 0 && <span className="placeholder">{placeholder ?? 'Alle'}</span>}
        {waarden.map((w) => (
          <span className="chip" key={w}>
            {opties.find((o) => o.waarde === w)?.label ?? w}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(waarden.filter((x) => x !== w));
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {open && (
        <div className="multi-lijst">
          <input autoFocus placeholder="Zoeken…" value={zoek} onChange={(e) => setZoek(e.target.value)} />
          {gefilterd.map((o) => (
            <label className="optie" key={o.waarde}>
              <input type="checkbox" checked={waarden.includes(o.waarde)} onChange={(e) => onChange(e.target.checked ? [...waarden, o.waarde] : waarden.filter((x) => x !== o.waarde))} />
              {o.label}
            </label>
          ))}
          {!gefilterd.length && <div className="optie mute">Niets gevonden</div>}
        </div>
      )}
    </div>
  );
}

export function Laden() {
  return <div className="leeg">Laden…</div>;
}

export function Tabs<T extends string>({ waarde, opties, onChange }: { waarde: T; opties: [T, ReactNode][]; onChange: (t: T) => void }) {
  return (
    <div className="tabs">
      {opties.map(([k, l]) => (
        <button key={k} className={waarde === k ? 'actief' : ''} onClick={() => onChange(k)}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function getal(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Tijdkeuze in kwartieren (zoals in de Power App). */
export const TIJDEN = Array.from({ length: 4 * 17 + 1 }, (_, i) => {
  const m = 6 * 60 + i * 15;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
});
